import { formatFormula, parseFormula } from './formulaUtils';

function greatestCommonDivisor(first, second) {
  return second === 0 ? Math.abs(first) : greatestCommonDivisor(second, first % second);
}

function leastCommonMultiple(first, second) {
  return Math.abs(first * second) / greatestCommonDivisor(first, second);
}

// Tiny exact fraction helpers, so balancing never suffers from floating-point error.
const createFraction = (numerator, denominator = 1) => {
  const sign = denominator < 0 ? -1 : 1;
  const divisor = greatestCommonDivisor(numerator, denominator) || 1;
  return { numerator: (sign * numerator) / divisor, denominator: (sign * denominator) / divisor };
};
const subtractFractions = (first, second) =>
  createFraction(
    first.numerator * second.denominator - second.numerator * first.denominator,
    first.denominator * second.denominator
  );
const multiplyFractions = (first, second) =>
  createFraction(first.numerator * second.numerator, first.denominator * second.denominator);
const divideFractions = (first, second) =>
  createFraction(first.numerator * second.denominator, first.denominator * second.numerator);
const isZero = (fraction) => fraction.numerator === 0;

/**
 * Finds the smallest whole-number coefficients that balance an equation.
 * balanceEquation(['H2', 'O2'], ['H2O']) -> [2, 1, 2]
 * Throws if the equation has no single balanced form.
 */
export function balanceEquation(reactantFormulas, productFormulas) {
  const species = [...reactantFormulas, ...productFormulas];
  const atomCounts = species.map(parseFormula);
  const symbols = [...new Set(atomCounts.flatMap((counts) => Object.keys(counts)))];

  // One row per element: reactant atoms count positive, product atoms negative.
  const matrix = symbols.map((symbol) =>
    atomCounts.map((counts, index) => createFraction((counts[symbol] ?? 0) * (index < reactantFormulas.length ? 1 : -1)))
  );

  // Reduced row echelon form.
  const pivotColumns = [];
  let pivotRow = 0;
  for (let column = 0; column < species.length && pivotRow < matrix.length; column++) {
    const currentRow = pivotRow;
    const rowWithValue = matrix.findIndex((row, rowIndex) => rowIndex >= currentRow && !isZero(row[column]));
    if (rowWithValue === -1) continue;
    [matrix[currentRow], matrix[rowWithValue]] = [matrix[rowWithValue], matrix[currentRow]];
    const pivotValue = matrix[currentRow][column];
    matrix[currentRow] = matrix[currentRow].map((value) => divideFractions(value, pivotValue));
    const pivotValues = matrix[currentRow];
    matrix.forEach((row, rowIndex) => {
      if (rowIndex === currentRow || isZero(row[column])) return;
      const factor = row[column];
      matrix[rowIndex] = row.map((value, valueIndex) => subtractFractions(value, multiplyFractions(factor, pivotValues[valueIndex])));
    });
    pivotColumns.push(column);
    pivotRow += 1;
  }

  const freeColumns = species.map((_, column) => column).filter((column) => !pivotColumns.includes(column));
  if (freeColumns.length !== 1) {
    throw new Error(`Cannot balance ${reactantFormulas.join(' + ')} -> ${productFormulas.join(' + ')}.`);
  }
  const [freeColumn] = freeColumns;
  const coefficients = species.map(() => createFraction(0));
  coefficients[freeColumn] = createFraction(1);
  pivotColumns.forEach((column, rowIndex) => {
    coefficients[column] = createFraction(-matrix[rowIndex][freeColumn].numerator, matrix[rowIndex][freeColumn].denominator);
  });

  const commonDenominator = coefficients.reduce((multiple, fraction) => leastCommonMultiple(multiple, fraction.denominator), 1);
  const wholeNumbers = coefficients.map((fraction) => (fraction.numerator * commonDenominator) / fraction.denominator);
  if (wholeNumbers.some((value) => value <= 0)) {
    throw new Error(`Cannot balance ${reactantFormulas.join(' + ')} -> ${productFormulas.join(' + ')}.`);
  }
  const divisor = wholeNumbers.reduce(greatestCommonDivisor);
  return wholeNumbers.map((value) => value / divisor);
}

/**
 * True when both sides have exactly the same atoms.
 */
export function isEquationBalanced(reactants, products) {
  const countSide = (side) =>
    side.reduce((totals, { coefficient, formula }) => {
      Object.entries(parseFormula(formula)).forEach(([symbol, count]) => {
        totals[symbol] = (totals[symbol] ?? 0) + count * coefficient;
      });
      return totals;
    }, {});
  const left = countSide(reactants);
  const right = countSide(products);
  const symbols = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...symbols].every((symbol) => left[symbol] === right[symbol]);
}

/**
 * Builds a balanced, display-ready equation: "2H₂ + O₂ → 2H₂O".
 * Returns { reactants: [{coefficient, formula}], products: [...], text }.
 */
export function createBalancedEquation(reactantFormulas, productFormulas) {
  const coefficients = balanceEquation(reactantFormulas, productFormulas);
  const reactants = reactantFormulas.map((formula, index) => ({ coefficient: coefficients[index], formula }));
  const products = productFormulas.map((formula, index) => ({
    coefficient: coefficients[reactantFormulas.length + index],
    formula,
  }));
  const formatSide = (side) =>
    side.map(({ coefficient, formula }) => `${coefficient > 1 ? coefficient : ''}${formatFormula(formula)}`).join(' + ');
  return { reactants, products, text: `${formatSide(reactants)} → ${formatSide(products)}` };
}
